import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "../../utils/axiosInstance"; 
import Swal from "sweetalert2";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import Assignment from "@mui/icons-material/Assignment";
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js"
import debounce from "lodash.debounce";

function SendNotification() {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const recipientTypes = ["Single User", "All Users"];
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  console.log(userOptions , "userOptionsuserOptions")
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    message: Yup.string().required("Message is required"),
    recipientType: Yup.string().required("Recipient Type is required"),
    userId: Yup.string().when("recipientType", {
      is: "Single User",
      then: (schema) => schema.required("User ID or Email is required").nullable(),
      otherwise: (schema) => schema.nullable()
    }),
    sendType: Yup.number().required("Send Type is required"),
    scheduledTime: Yup.date().when("sendType", {
      is: 1,
      then: (schema) => schema.required("Scheduled time is required"),
      otherwise: (schema) => schema.nullable()
    }),
    link: Yup.string().url("Invalid URL").required("Link is required"),
  });
  const formik = useFormik({
    initialValues: {
      title: "",
      message: "",
      recipientType: "",
      userId: "",
      selectedGroups: [],
      sendType: "",
      scheduledTime: "",
      link: ""
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
        scheduleTime:
          values.sendType === 1 && values.scheduledTime
            ? values.scheduledTime.toISOString()
            : null
      };
      console.log("Payload to send:", payload);
      await sendNotification(payload);
    }
  });
  const sendNotification = async (data) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL_PREFIX}/api/v1/admin/send-notification`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json"
          }
        }
      );
      setLoading(false);
      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data.message,
        showConfirmButton: false,
        timer: 1500
      });
      formik.resetForm();
    } catch (error) {
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Something went wrong",
        showConfirmButton: false,
        dangerMode: true
      });
    }
  };
  const debouncedFetchUsers = useMemo(() =>
    debounce(async (searchText) => {
      if (!searchText) return;
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_URL_PREFIX}/api/v1/admin/enduserlist`, {
          params: { search: searchText },
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserOptions(response.data.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoadingUsers(false);
      }
    }, 500), []
  );

  useEffect(() => {
    if (inputValue) {
      debouncedFetchUsers(inputValue);
    } else {
      setUserOptions([]);
    }
  }, [inputValue]);
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Send Notifications</h4>
          </CardHeader>
          <CardBody>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <form onSubmit={formik.handleSubmit}>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        label="Title"
                        fullWidth
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        error={formik.touched.title && Boolean(formik.errors.title)}
                        helperText={formik.touched.title && formik.errors.title}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Message"
                        fullWidth
                        multiline
                        minRows={3}
                        name="message"
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        error={formik.touched.message && Boolean(formik.errors.message)}
                        helperText={formik.touched.message && formik.errors.message}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth error={formik.touched.recipientType && Boolean(formik.errors.recipientType)}>
                        <InputLabel>Recipient Type</InputLabel>
                        <Select
                          name="recipientType"
                          value={formik.values.recipientType}
                          onChange={formik.handleChange}
                          label="Recipient Type"
                        >
                          {recipientTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.recipientType && (
                          <Typography variant="caption" color="error">
                            {formik.errors.recipientType}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* {formik.values.recipientType == "Single User" && (
                      <Grid item xs={12}>
                        <TextField
                          label="User ID or Email"
                          fullWidth
                          name="target"
                          value={formik.values.target}
                          onChange={formik.handleChange}
                          error={formik.touched.target && Boolean(formik.errors.target)}
                          helperText={formik.touched.target && formik.errors.target}
                        />
                      </Grid>
                    )} */}

                    {formik.values.recipientType === "Single User" && (
                      <Grid item xs={12}>
                        <Autocomplete
                          fullWidth
                          options={userOptions}
                          getOptionLabel={(option) => option.fullName || option.emailAddress}
                          filterOptions={(options) => options} 
                          loading={loadingUsers}
                          onInputChange={(event, newInputValue) => {
                            setInputValue(newInputValue);
                            // formik.setFieldValue("userId", newInputValue);
                          }}
                          onChange={(event, selectedOption) => {
                            formik.setFieldValue("userId", selectedOption?._id || "");
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="User ID or Email"
                              error={formik.touched.userId && Boolean(formik.errors.userId)}
                              helperText={formik.touched.userId && formik.errors.userId}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingUsers ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <FormControl fullWidth error={formik.touched.sendType && Boolean(formik.errors.sendType)}>
                        <InputLabel>Send Type</InputLabel>
                        <Select
                          name="sendType"
                          value={formik.values.sendType}
                          onChange={(event) => formik.setFieldValue("sendType", Number(event.target.value))}
                          label="Send Type"

                        >
                          <MenuItem value={0}>Instant</MenuItem>
                          <MenuItem value={1}>Scheduled</MenuItem>
                        </Select>
                        {formik.touched.sendType && (
                          <Typography variant="caption" color="error">
                            {formik.errors.sendType}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {formik.values.sendType === 1 && (
                      <Grid item xs={12}>
                        <DateTimePicker
                          label="Scheduled Date & Time"
                          value={formik.values.scheduledTime}
                          onChange={(newValue) => {
                            formik.setFieldValue("scheduledTime", newValue);
                            formik.setFieldTouched("scheduledTime", true);
                            formik.validateField("scheduledTime");
                          }}
                          minDateTime={new Date()}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={formik.touched.scheduledTime && Boolean(formik.errors.scheduledTime)}
                              helperText={formik.touched.scheduledTime && formik.errors.scheduledTime}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        label="Optional Link"
                        fullWidth
                        name="link"
                        value={formik.values.link}
                        onChange={formik.handleChange}
                        error={formik.touched.link && Boolean(formik.errors.link)}
                        helperText={formik.touched.link && formik.errors.link}
                        placeholder="https://example.com"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={loading}
                        sx={{ py: 1.5 }}
                      >
                        {loading ? "Sending..." : "Send Notification"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            </LocalizationProvider>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

export default SendNotification;
